import { Component, forwardRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormsModule,
    ReactiveFormsModule,
    NG_VALUE_ACCESSOR,
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { lastValueFrom } from 'rxjs';
import { QuestionGroupsService } from '../../services/question-groups.service';
import { QuestionGroupListDto, CreateQuestionGroupLinkDto } from '../../../../core/data/new-dtos/insurance-type.dtos';
import { SharedModule } from '../../../../common/shared.module';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QuestionGroupModalComponent } from '../question-group-modal/question-group-modal.component';

@Component({
    selector: 'app-question-group-selector',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, NgSelectModule, SharedModule],
    templateUrl: './question-group-selector.component.html',
    styleUrls: ['./question-group-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuestionGroupSelectorComponent),
            multi: true,
        },
    ],
})
export class QuestionGroupSelectorComponent implements ControlValueAccessor, OnInit {
    questionGroups: QuestionGroupListDto[] = [];
    selectedLinks: CreateQuestionGroupLinkDto[] = [];
    disabled = false;
    loading = false;

    // Stable property for NgSelect
    selectedIds: number[] = [];

    syncSelectedIds() {
        this.selectedIds = this.selectedLinks.map(l => l.questionGroupId);
    }

    phaseOptions = [
        { label: 'En el checkout', value: 'Checkout' },
        { label: 'Al Seleccionar', value: 'Selection' }
    ];

    private onChange: (value: any[]) => void = () => { };
    private onTouched: () => void = () => { };

    constructor(
        private questionGroupsService: QuestionGroupsService,
        private fb: FormBuilder,
        private modalService: NgbModal,
    ) { }

    async ngOnInit() {
        await this.loadQuestionGroups();
    }

    async loadQuestionGroups() {
        this.loading = true;
        try {
            this.questionGroups = await lastValueFrom(this.questionGroupsService.getAll());
        } catch (e) {
            console.error('Error loading question groups', e);
        } finally {
            this.loading = false;
        }
    }

    // Supports both legacy number[] and new link objects
    writeValue(value: any[] | null): void {
        if (!value) {
            this.selectedLinks = [];
            return;
        }
        if (value.length > 0 && typeof value[0] === 'number') {
            // Legacy conversion
            this.selectedLinks = (value as number[]).map(id => ({
                questionGroupId: id,
                phase: 'Checkout'
            }));
        } else {
            this.selectedLinks = (value as CreateQuestionGroupLinkDto[]) || [];
        }
        this.syncSelectedIds();
    }

    registerOnChange(fn: (value: any[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private emitChange() {
        this.onChange(this.selectedLinks);
        this.onTouched();
    }

    onSelectionChange(ids: number[]): void {
        this.selectedIds = ids;

        // IDs comes from NgSelect
        // We need to sync selectedLinks
        const currentIds = new Set(this.selectedLinks.map(l => l.questionGroupId));
        const newIds = new Set(ids);

        // Remove unselected
        this.selectedLinks = this.selectedLinks.filter(l => newIds.has(l.questionGroupId));

        // Add new
        ids.forEach(id => {
            if (!currentIds.has(id)) {
                this.selectedLinks.push({
                    questionGroupId: id,
                    phase: 'Checkout' // Default
                });
            }
        });

        this.emitChange();
    }

    onPhaseChange(index: number, phase: string): void {
        this.selectedLinks[index].phase = phase;
        this.emitChange();
    }

    removeLink(index: number): void {
        this.selectedLinks.splice(index, 1);
        this.syncSelectedIds();
        this.emitChange();
    }

    getGroupTitle(id: number): string {
        return this.questionGroups.find(g => g.id === id)?.title || `Grupo #${id}`;
    }

    async openCreateModal() {
        try {
            const modalRef = this.modalService.open(QuestionGroupModalComponent, { size: 'lg' });
            const result = await modalRef.result;

            if (result && result.id) {
                await this.loadQuestionGroups();

                // Add new group to selected links
                this.selectedLinks = [...this.selectedLinks, {
                    questionGroupId: result.id,
                    phase: result.phase || 'Checkout'
                }];
                this.syncSelectedIds();
                this.emitChange();
            }
        } catch (e) {
            // Modal dismissed
        }
    }

    async editGroup(link: CreateQuestionGroupLinkDto) {
        try {
            const modalRef = this.modalService.open(QuestionGroupModalComponent, { size: 'lg' });
            modalRef.componentInstance.groupId = link.questionGroupId;
            modalRef.componentInstance.initialPhase = link.phase;
            const result = await modalRef.result;

            if (result && result.id) {
                await this.loadQuestionGroups();
                // Phase might have been updated conceptually, but typically we keep local override.
                // Or if the modal returns phase, we update it.
                // The Modal returns {id, phase}.
                const index = this.selectedLinks.findIndex(l => l.questionGroupId === result.id);
                if (index !== -1) {
                    // Update phase? Maybe user wants to change it too.
                    // But phase is property of the LINK, not the GROUP.
                    // The modal has a phase input. If we prefill it, we can return it.
                    // But we didn't add logic to prefill phase in modal from Input (only from form default).
                    // For now, let's just refresh titles/descriptions.
                }
            }
        } catch (e) {
            // Modal dismissed
        }
    }
}
